/*
============================================================
 APIDataSource - Loads puzzle data from remote API
------------------------------------------------------------
 Implements the data source interface for database-backed
 puzzles accessed via REST API.
============================================================
*/

/**
 * Data source for loading puzzles from a remote API
 * @class APIDataSource
 */
export class APIDataSource {
  /**
   * @param {string} apiHost - Base URL for API (e.g., 'https://api.example.com')
   */
  constructor(apiHost) {
    this.apiHost = apiHost;
    this.apiPath = "/api/daily_db_puzzle.php";
  }

  /**
   * Build the full API URL
   * @private
   * @returns {string} The full API endpoint URL
   */
  _buildUrl() {
    if (this.apiHost) {
      return `${this.apiHost}${this.apiPath}`;
    }
    // Use relative path (same domain)
    return this.apiPath;
  }

  /**
   * Fetch puzzle data from the remote API
   * @async
   * @param {string} dateStr - Puzzle date in YYYYMMDD format
   * @param {string} userTimezone - User's timezone string
   * @returns {Promise<Object>} The puzzle data object
   * @throws {Error} If request fails or data is invalid
   */
  async fetch(dateStr, userTimezone) {
    if (!dateStr) {
      throw new Error("Date string is required");
    }

    const url = this._buildUrl();
    console.log(`[APIDataSource] API endpoint: ${url}`);
    console.log(`[APIDataSource] Requesting puzzle for date: ${dateStr}`);

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          filename: dateStr,
          userTimezone: userTimezone || "UTC",
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: API request failed`);
      }

      const data = await response.json();

      // Validate that API returned actual puzzle data, not error message
      if (!data || !data.metadata || !data.finmodel) {
        if (data && data.error) {
          throw new Error(`API error: ${data.error}`);
        }
        throw new Error("Invalid puzzle data structure from API");
      }

      return data;
    } catch (error) {
      console.error(
        `[APIDataSource] Error loading puzzle for ${dateStr}:`,
        error.message
      );
      throw error;
    }
  }
}
